/**
 * cs-polish.js — image-mode switch + manual colour-scheme override.
 *
 * Colour scheme itself is decided by prefers-color-scheme in cs-dark.css: no JS, applied at
 * first paint. This file only persists a manual override, which does need storage. The old
 * clock-driven version ran after paint (flash), re-checked every 60s (could flip mid-browse),
 * and guessed at what the OS already reports.
 */
(() => {
  const root = document.documentElement;
  const darkQuery = matchMedia('(prefers-color-scheme: dark)');
  const IMAGE_KEY = 'cs-image-mode';
  const THEME_KEY = 'cs-theme-override';

  const read = (key, valid) => {
    try {
      const v = localStorage.getItem(key);
      return valid.includes(v) ? v : null;
    } catch (_) {
      return null;
    }
  };

  const write = (key, value) => {
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    } catch (_) {
      /* storage blocked; the attribute still applies for this page view */
    }
  };

  // Product cards show either a plain pack shot or a styled variant.
  // Motion is the default: the styled shot reads more naturally than a flat pack shot, and a
  // visitor who has never touched the toggle should see the better one.
  const applyImageMode = () => {
    const mode = read(IMAGE_KEY, ['motion', 'plain']) || 'motion';
    root.setAttribute('data-cs-image-mode', mode);
    document.querySelectorAll('[data-cs-image-mode-option]').forEach((b) => {
      b.setAttribute('aria-pressed', String(b.dataset.csImageModeOption === mode));
    });
  };

  const systemTheme = () => (darkQuery.matches ? 'dark' : 'light');
  const currentTheme = () => read(THEME_KEY, ['dark', 'light']) || systemTheme();

  // The accessible name states what the button will do, which flips with the current
  // appearance — one static label would be wrong half the time.
  const syncToggleLabel = () => {
    const isDark = currentTheme() === 'dark';
    document.querySelectorAll('[data-cs-theme-toggle]').forEach((b) => {
      b.setAttribute('aria-pressed', String(isDark));
      const label = isDark ? b.dataset.labelLight : b.dataset.labelDark;
      if (label) b.setAttribute('aria-label', label);
    });
  };

  // Written explicitly as 'dark' or 'light' — never removed-to-mean-light — because the
  // attribute must beat the media query in both directions.
  const applyThemeOverride = () => {
    const override = read(THEME_KEY, ['dark', 'light']);
    if (override) root.setAttribute('data-cs-theme', override);
    else root.removeAttribute('data-cs-theme');
    syncToggleLabel();
  };

  document.addEventListener('click', (event) => {
    const imageBtn = event.target.closest('[data-cs-image-mode-option]');
    if (imageBtn) {
      write(IMAGE_KEY, imageBtn.dataset.csImageModeOption === 'motion' ? 'motion' : 'plain');
      applyImageMode();
      return;
    }

    if (event.target.closest('[data-cs-theme-toggle]')) {
      const next = currentTheme() === 'dark' ? 'light' : 'dark';
      // Choosing what the OS already reports clears the override rather than pinning it.
      write(THEME_KEY, next === systemTheme() ? null : next);
      applyThemeOverride();
    }
  });

  // CSS has already repainted on an OS flip; only the label needs correcting.
  darkQuery.addEventListener('change', syncToggleLabel);

  applyImageMode();
  applyThemeOverride();

  document.addEventListener('shopify:section:load', () => {
    applyImageMode();
    applyThemeOverride();
  });
})();
